import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, AlertCircle, Bell, CheckCircle, FileText } from 'react-feather';
import { Link } from 'react-router-dom';
import { LI, P, UL } from '../../../../AbstractElements';
import i18n from '../../../../i18n';
import { loadDashboardActivityData } from '../../MainPage/activityData';
import {
    buildVehicleAlertNotificationItems,
    getVehicleAlertsPath,
    loadVehicleNoticeAlerts,
} from '../../VehicleAlerts/vehicleAlertsData';
import {
    NOTIFICATION_LIMIT,
    buildNotificationItems,
    formatNotificationAge,
    getNotificationHeaderLabel,
    sortNotificationsNewestFirst,
} from './notificationsData';
import AvisDetailModal from '../../Avis/AvisDetailModal';

const notificationIcons = {
    primary: FileText,
    secondary: Activity,
    success: CheckCircle,
    danger: AlertCircle,
};

const NotificationContent = ({ Icon, notification, ageLabel }) => (
    <div className="media">
        <span className={`notification-bg bg-light-${notification.variant}`}>
            <Icon />
        </span>
        <div className="media-body">
            <P>{notification.title}</P>
            {notification.description && <span>{notification.description}</span>}
            {ageLabel && <span className="d-block">{ageLabel}</span>}
        </div>
    </div>
);

const Notifications = () => {
    const dropdownRef = useRef(null);
    const [activities, setActivities] = useState([]);
    const [vehicleAlerts, setVehicleAlerts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedAvisId, setSelectedAvisId] = useState('');

    useEffect(() => {
        let isMounted = true;

        const loadNotifications = async () => {
            setIsLoading(true);
            setHasError(false);

            try {
                const [activityResult, vehicleAlertsResult] = await Promise.allSettled([
                    loadDashboardActivityData(),
                    loadVehicleNoticeAlerts({ includeLegacyTickets: false }),
                ]);

                if (isMounted) {
                    setActivities(activityResult.status === 'fulfilled'
                        ? activityResult.value.activities || []
                        : []);
                    setVehicleAlerts(vehicleAlertsResult.status === 'fulfilled'
                        ? vehicleAlertsResult.value.alerts || []
                        : []);
                    setHasError(activityResult.status === 'rejected' && vehicleAlertsResult.status === 'rejected');
                }
            } catch (error) {
                if (isMounted) {
                    setActivities([]);
                    setVehicleAlerts([]);
                    setHasError(true);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadNotifications();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const closeOnOutsideClick = event => {
            if (!isOpen || !dropdownRef.current || dropdownRef.current.contains(event.target)) {
                return;
            }

            setIsOpen(false);
        };

        document.addEventListener('mousedown', closeOnOutsideClick);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
        };
    }, [isOpen]);

    const notifications = useMemo(() => {
        const vehicleAlertItems = buildVehicleAlertNotificationItems(vehicleAlerts, {
            publicUrl: process.env.PUBLIC_URL,
        }) || [];
        const activityItems = buildNotificationItems(activities, {
            fallbackTitle: i18n.t('Dashboard_Notifications_RecentActivity'),
            publicUrl: process.env.PUBLIC_URL,
            limit: NOTIFICATION_LIMIT,
        });

        return sortNotificationsNewestFirst([...vehicleAlertItems, ...activityItems])
            .slice(0, NOTIFICATION_LIMIT);
    }, [activities, vehicleAlerts]);

    const notificationCount = notifications.length;
    const hasNotifications = !isLoading && !hasError && notificationCount > 0;
    const translate = i18n.t.bind(i18n);
    const currentTimestamp = Date.now();
    const headerLabel = isLoading
        ? i18n.t('Dashboard_Notifications_Loading')
        : hasError
            ? i18n.t('Dashboard_Notifications_LoadError')
            : getNotificationHeaderLabel(notificationCount, translate);

    const closeDropdown = () => setIsOpen(false);
    const openAvisModal = avisId => {
        closeDropdown();
        setSelectedAvisId(avisId);
    };

    const handleKeyDown = event => {
        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <Fragment>
            <li className="onhover-dropdown maxpark-notifications" ref={dropdownRef} onKeyDown={handleKeyDown}>
                <button
                    type="button"
                    className="notification-box maxpark-notification-toggle"
                    aria-label={i18n.t('Dashboard_Notifications_Label')}
                    aria-expanded={isOpen}
                    aria-controls="maxpark-notification-menu"
                    onClick={() => setIsOpen(open => !open)}
                >
                    <Bell />
                    {hasNotifications && <span className="dot-animated"></span>}
                </button>
                <UL attrUL={{ id: 'maxpark-notification-menu', className: `notification-dropdown onhover-show-div ${isOpen ? 'active' : ''}`, 'aria-busy': isLoading }} >
                    <LI>
                        <P attrPara={{ className: 'f-w-700 m-0' }} >
                            {headerLabel}
                            {!isLoading && !hasError && (
                                <span className="pull-right badge badge-primary badge-pill">{notificationCount}</span>
                            )}
                        </P>
                    </LI>
                    {isLoading && (
                        <LI attrLI={{ className: 'text-center maxpark-notification-state' }} >
                            <P>{i18n.t('Dashboard_Notifications_Loading')}</P>
                        </LI>
                    )}
                    {!isLoading && hasError && (
                        <LI attrLI={{ className: 'noti-danger maxpark-notification-state' }} >
                            <div className="media">
                                <span className="notification-bg bg-light-danger">
                                    <AlertCircle />
                                </span>
                                <div className="media-body">
                                    <P>{i18n.t('Dashboard_Notifications_LoadError')}</P>
                                    <span>{i18n.t('Dashboard_Notifications_RetryLater')}</span>
                                </div>
                            </div>
                        </LI>
                    )}
                    {!isLoading && !hasError && notificationCount === 0 && (
                        <LI attrLI={{ className: 'text-center maxpark-notification-state' }} >
                            <P>{i18n.t('Dashboard_Notifications_Empty')}</P>
                        </LI>
                    )}
                    {!isLoading && !hasError && notifications.map(notification => {
                        const Icon = notificationIcons[notification.variant] || FileText;
                        const ageLabel = formatNotificationAge(notification.timestamp, currentTimestamp, translate) || notification.fallbackTime;

                        return (
                            <LI attrLI={{ className: `noti-${notification.variant}` }} key={notification.id}>
                                {notification.ticketNumber ? (
                                    <button
                                        type="button"
                                        className="maxpark-notification-action"
                                        onClick={() => openAvisModal(notification.ticketNumber)}
                                        aria-label={`Ouvrir l'avis ${notification.ticketNumber}`}
                                    >
                                        <NotificationContent Icon={Icon} notification={notification} ageLabel={ageLabel} />
                                    </button>
                                ) : (
                                    <Link to={notification.path} onClick={closeDropdown}>
                                        <NotificationContent Icon={Icon} notification={notification} ageLabel={ageLabel} />
                                    </Link>
                                )}
                            </LI>
                        );
                    })}
                    {hasNotifications && (
                        <LI attrLI={{ className: 'text-center' }} >
                            <Link className="f-w-700" to={vehicleAlerts.length ? getVehicleAlertsPath(process.env.PUBLIC_URL) : `${process.env.PUBLIC_URL}/dashboard/avis`} onClick={closeDropdown}>
                                {i18n.t('Dashboard_Notifications_SeeAll')}</Link>
                        </LI>
                    )}
                </UL>
            </li>
            <AvisDetailModal
                avisId={selectedAvisId}
                isOpen={Boolean(selectedAvisId)}
                toggle={() => setSelectedAvisId('')}
            />
        </Fragment>
    );
};

export default Notifications;
